/**
 * Multi-segment distance tool with persistent measurements.
 *
 *   Left-click  → drop a new vertex (extends the current measurement)
 *   Right-click → finalise the current measurement; it stays on the
 *                 scene and gets registered with the manager so it
 *                 shows up in the Ebenen-Panel.  Next left-click
 *                 starts a fresh measurement (the previous one stays
 *                 visible).
 *
 *   - The Σ total label is only added when there are ≥3 vertices
 *     (i.e. more than one segment).  For a single segment the
 *     segment label already contains the full length.
 *   - Lines use LineMaterial(worldUnits: true) so they get thicker
 *     close-up and slimmer from far away — matches the desktop viewer.
 *   - Each finished measurement lives in its own Three.js Group so
 *     the Ebenen-Panel can toggle visibility independently.
 */

import {
  Color,
  Object3D,
  Scene as ThreeScene,
  Sprite,
  SpriteMaterial,
  Texture,
  Vector3,
} from 'three'
import { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'

import { buildMarkerCone } from './markerCone'
import { Measurement, nextId } from '../measurements'

const COLOR_OUTLINE = 0x000000
// World-space line thickness in metres — verbatim from 3DRoad markers.js
// (lineMat 0.0048 + outlineMat 0.006).
const LINEWIDTH_M = 0.009
const OUTLINE_WIDTH_M = 0.012
/** Base marker radius — same as 3DRoad core/loader-wrapper.js
 * (markerRadius = 0.09 m). */
const MARKER_RADIUS = 0.09

export type MeasurementListener = (event: { added?: Measurement; removed?: string; updated?: Measurement }) => void

export class DistanceTool {
  private current: { points: Vector3[]; group: Object3D } | null = null
  private color: number
  private listener: MeasurementListener | null = null
  /** Counter for German naming ("Linie 1", "Linie 2", …) */
  private serial = 0
  /** Multiplier on LINEWIDTH_M / OUTLINE_WIDTH_M (live-adjustable). */
  private lineScale = 1
  /** Multiplier on label base size (live-adjustable). */
  private labelScale = 1

  constructor(private scene: ThreeScene, color: number = 0xffd400) {
    this.color = color
  }

  setColor(color: number): void {
    this.color = color
  }

  /** Scale every measurement-label sprite (existing + new). */
  setLabelScale(scale: number): void {
    this.labelScale = scale
    this.scene.traverse((child) => {
      const ud = (child as Sprite).userData as
        | { baseScaleX?: number; baseScaleY?: number; isMeasureLabel?: boolean }
        | undefined
      if (
        ud?.isMeasureLabel &&
        typeof ud.baseScaleX === 'number' &&
        typeof ud.baseScaleY === 'number'
      ) {
        ;(child as Sprite).scale.set(ud.baseScaleX * scale, ud.baseScaleY * scale, 1)
      }
    })
  }

  /** Scale all line thicknesses (existing + new). 1.0 = stock 3DRoad. */
  setLineScale(scale: number): void {
    this.lineScale = scale
    this.scene.traverse((child) => {
      const m = (child as Line2).material as (LineMaterial & { userData?: { baseLinewidth?: number } }) | undefined
      if (m && (m as LineMaterial).isLineMaterial) {
        const base = (m as unknown as { userData: { baseLinewidth?: number } }).userData?.baseLinewidth
        if (typeof base === 'number') m.linewidth = base * scale
      }
    })
  }

  setListener(fn: MeasurementListener | null): void {
    this.listener = fn
  }

  setResolution(width: number, height: number): void {
    this.scene.traverse((child) => {
      const m = (child as Line2).material as LineMaterial | undefined
      if (m && m.resolution && (m as LineMaterial).isLineMaterial) {
        m.resolution.set(width, height)
      }
    })
  }

  /** Drop a new vertex.  Starts a fresh measurement if none in progress. */
  handleClick(point: Vector3): void {
    if (!this.current) {
      const group = new Object3D()
      group.name = 'distance-current'
      this.scene.add(group)
      this.current = { points: [], group }
    }
    this.current.points.push(point.clone())
    this.addMarker(this.current.group, point)
    if (this.current.points.length >= 2) {
      const prev = this.current.points[this.current.points.length - 2]
      const curr = this.current.points[this.current.points.length - 1]
      this.addSegment(this.current.group, prev, curr)
    }
  }

  /** Right-click / Enter / Escape — finalise the current measurement. */
  finish(): void {
    if (!this.current || this.current.points.length < 2) {
      // Nothing useful to keep — clean up the partial draw if any
      if (this.current) {
        this.scene.remove(this.current.group)
      }
      this.current = null
      return
    }

    const points = this.current.points
    const group = this.current.group
    const total = this.computeTotal(points)

    // Only add the Σ label when there are multiple segments — for a
    // single-segment measurement the per-segment label already shows
    // the exact same value.
    if (points.length >= 3) {
      const last = points[points.length - 1]
      const prev = points[points.length - 2]
      // Anchor the Σ label past the last endpoint, in the direction the
      // last segment was heading — so the endpoint marker stays visible
      // regardless of line thickness.  Fallback to pure Z lift if the
      // last segment is degenerate.
      const dir = last.clone().sub(prev)
      const len = dir.length()
      // Negative → point back along the segment, so the label sits over
       // the line it summarises instead of floating off into empty space.
      if (len > 1e-6) dir.multiplyScalar(-0.35 / len)
      else dir.set(0, 0, 0)
      const anchor = last.clone().add(dir).add(new Vector3(0, 0, MARKER_RADIUS * 4))
      const sumSprite = makePixelLabel(`Σ ${total.toFixed(2)} m`, anchor, this.color, 0x000000, true)
      sumSprite.userData.isMeasureLabel = true
    sumSprite.scale.multiplyScalar(this.labelScale)
      group.add(sumSprite)
    }

    this.serial += 1
    const measurement: Measurement = {
      id: nextId(),
      kind: 'distance',
      label: `Linie ${this.serial} — ${total.toFixed(2)} m`,
      visible: true,
      group,
      color: this.color,
    }
    group.name = `distance-${measurement.id}`
    group.userData.measurementId = measurement.id
    this.current = null
    this.listener?.({ added: measurement })
  }

  removeMeasurement(id: string, group: Object3D): void {
    this.scene.remove(group)
    disposeChildren(group)
    this.listener?.({ removed: id })
  }

  /** Bump the per-session serial — used after restoring existing
   * annotations so freshly created ones don't collide with "Linie 1". */
  setSerial(value: number): void {
    if (value > this.serial) this.serial = value
  }

  /** Build a finished distance group from a saved payload, attach it to the
   * scene, and return it for inclusion in the measurements state. Mirrors
   * the geometry produced by `handleClick` + `finish` (markers, segments,
   * Σ label when ≥3 points). */
  buildGroupFromPayload(points: Vector3[], color: number): { group: Object3D; total: number } {
    if (points.length < 2) {
      throw new Error('distance annotation needs ≥ 2 points')
    }
    const group = new Object3D()
    this.scene.add(group)

    // Save & temporarily swap the color so the existing private helpers
    // paint with the restored measurement's hue, not the live picker.
    const previousColor = this.color
    this.color = color
    try {
      for (const p of points) this.addMarker(group, p)
      for (let i = 1; i < points.length; i++) {
        this.addSegment(group, points[i - 1], points[i])
      }

      const total = this.computeTotal(points)
      if (points.length >= 3) {
        const last = points[points.length - 1]
        const prev = points[points.length - 2]
        const dir = last.clone().sub(prev)
        const len = dir.length()
        if (len > 1e-6) dir.multiplyScalar(-0.35 / len)
        else dir.set(0, 0, 0)
        const anchor = last.clone().add(dir).add(new Vector3(0, 0, MARKER_RADIUS * 4))
        const sumSprite = makePixelLabel(`Σ ${total.toFixed(2)} m`, anchor, color, 0x000000, true)
        sumSprite.userData.isMeasureLabel = true
        sumSprite.scale.multiplyScalar(this.labelScale)
        group.add(sumSprite)
      }
      return { group, total }
    } finally {
      this.color = previousColor
    }
  }

  /** Wipe everything (active + finished — finished entries are tracked
   * by the Scene, which removes their groups via removeMeasurement). */
  clearActive(): void {
    if (!this.current) return
    this.scene.remove(this.current.group)
    disposeChildren(this.current.group)
    this.current = null
  }

  dispose(): void {
    this.clearActive()
  }

  // ── private ───────────────────────────────────────────────────────────

  private computeTotal(points: Vector3[]): number {
    let sum = 0
    for (let i = 1; i < points.length; i++) {
      sum += points[i - 1].distanceTo(points[i])
    }
    return sum
  }

  private addMarker(group: Object3D, point: Vector3): void {
    group.add(buildMarkerCone(point, this.color, MARKER_RADIUS))
  }

  private addSegment(group: Object3D, a: Vector3, b: Vector3): void {
    const positions = [a.x, a.y, a.z, b.x, b.y, b.z]

    // Black outline
    const outlineGeom = new LineGeometry()
    outlineGeom.setPositions(positions)
    const outlineMat = new LineMaterial({
      color: COLOR_OUTLINE,
      linewidth: OUTLINE_WIDTH_M * this.lineScale,
      worldUnits: true,
      depthTest: false,
      transparent: true,
    })
    outlineMat.userData.baseLinewidth = OUTLINE_WIDTH_M
    outlineMat.resolution.set(window.innerWidth, window.innerHeight)
    const outline = new Line2(outlineGeom, outlineMat)
    outline.computeLineDistances()
    outline.renderOrder = 999
    group.add(outline)

    // Coloured main line
    const geom = new LineGeometry()
    geom.setPositions(positions)
    const mat = new LineMaterial({
      color: this.color,
      linewidth: LINEWIDTH_M * this.lineScale,
      worldUnits: true,
      depthTest: false,
      transparent: true,
    })
    mat.userData.baseLinewidth = LINEWIDTH_M
    mat.resolution.set(window.innerWidth, window.innerHeight)
    const line = new Line2(geom, mat)
    line.computeLineDistances()
    line.renderOrder = 1000
    group.add(line)

    // Per-segment length label (lifted a few centimetres so it sits
    // above the surface and clears the cone tip).
    const mid = a.clone().add(b).multiplyScalar(0.5)
    mid.z += MARKER_RADIUS * 1.2
    const length = a.distanceTo(b)
    const segSprite = makePixelLabel(`${length.toFixed(2)} m`, mid, this.color, 0x000000, false)
    segSprite.userData.isMeasureLabel = true
    segSprite.scale.multiplyScalar(this.labelScale)
    group.add(segSprite)
  }
}

// ─── shared helpers ─────────────────────────────────────────────────────────

/** World-space height (metres) of a label at scale = 1.0.  Slider scales this. */
export const LABEL_BASE_HEIGHT_M = 0.5

/** Author chip baked into the top-right corner of a measurement label.
 * `null` / `undefined` means no chip — the label is rendered plain. */
export interface AuthorChip {
  initials: string
  color: number
}

export function makePixelLabel(
  text: string,
  position: Vector3,
  bg: number,
  fg: number,
  bold: boolean,
  fontSizeCss: number = 14,
  chip?: AuthorChip | null,
): Sprite {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const fontSize = Math.round(fontSizeCss * dpr)
  const paddingCss = 5
  const padding = Math.round(paddingCss * dpr)
  const heightCss = fontSizeCss + paddingCss * 2

  const measure = document.createElement('canvas').getContext('2d')!
  measure.font = `${bold ? 'bold ' : ''}${fontSize}px -apple-system, "SF Pro Text", sans-serif`
  const textWidthPx = Math.ceil(measure.measureText(text).width)

  // Circular chip lives INSIDE the rounded rect, tucked into the upper-
  // right corner. The rect is widened on the right so the chip never
  // covers any digits of the value text — it only ever overlaps padding.
  const chipDiameterCss = chip ? Math.round(heightCss * 0.78) : 0
  const chipGapCss = chip ? 3 : 0
  const rightPadCss = chip ? chipDiameterCss + chipGapCss + 2 : paddingCss
  const widthCss = Math.ceil(textWidthPx / dpr) + paddingCss + rightPadCss

  const canvas = document.createElement('canvas')
  canvas.width = widthCss * dpr
  canvas.height = heightCss * dpr
  const ctx = canvas.getContext('2d')!
  ctx.font = `${bold ? 'bold ' : ''}${fontSize}px -apple-system, "SF Pro Text", sans-serif`

  ctx.fillStyle = '#' + new Color(bg).getHexString()
  roundedRect(ctx, 0, 0, canvas.width, canvas.height, 6 * dpr)
  ctx.fill()

  // Text outline (1.5 px) for legibility on any background — then fill on top.
  ctx.textBaseline = 'middle'
  ctx.lineWidth = 1.5 * dpr
  ctx.strokeStyle = '#' + new Color(bg).getHexString()
  ctx.strokeText(text, padding, canvas.height / 2 + 1)
  ctx.fillStyle = '#' + new Color(fg).getHexString()
  ctx.fillText(text, padding, canvas.height / 2 + 1)

  if (chip) {
    drawAuthorCircle(ctx, dpr, canvas.width, chipDiameterCss, chip)
  }

  const tex = new Texture(canvas)
  tex.needsUpdate = true
  const mat = new SpriteMaterial({
    map: tex,
    transparent: true,
    sizeAttenuation: true, // shrink with distance — perspective scaling
    depthTest: false,
    depthWrite: false,
  })
  const sprite = new Sprite(mat)
  sprite.position.copy(position)
  const baseY = LABEL_BASE_HEIGHT_M
  const baseX = baseY * (widthCss / heightCss)
  sprite.userData.baseScaleX = baseX
  sprite.userData.baseScaleY = baseY
  sprite.scale.set(baseX, baseY, 1)
  sprite.renderOrder = 9999
  // Stash the inputs so we can rebuild this label later (toggle, author
  // arrives via WS, etc.) without the caller having to remember them.
  sprite.userData.labelSpec = {
    kind: 'pixel',
    text,
    bg,
    fg,
    bold,
    fontSizeCss,
    chip: chip ?? null,
  }
  sprite.userData.labelAnchor = position.clone()
  return sprite
}

/** Paint a circular author chip in the upper-right corner of a label canvas.
 * Exported so the multi-line `makeWrappedLabel` (Notiz) can use the same
 * visual treatment. */
export function drawAuthorCircle(
  ctx: CanvasRenderingContext2D,
  dpr: number,
  canvasWidthPx: number,
  chipDiameterCss: number,
  chip: AuthorChip,
): void {
  const diameter = chipDiameterCss * dpr
  const margin = 3 * dpr
  const cx = canvasWidthPx - margin - diameter / 2
  const cy = margin + diameter / 2

  ctx.fillStyle = '#' + new Color(chip.color).getHexString()
  ctx.beginPath()
  ctx.arc(cx, cy, diameter / 2, 0, Math.PI * 2)
  ctx.fill()
  // Slim white ring so the chip stays legible on a same-colour label bg.
  ctx.strokeStyle = 'rgba(255,255,255,0.95)'
  ctx.lineWidth = Math.max(1, dpr)
  ctx.stroke()

  const fontPx = Math.max(8 * dpr, Math.round(diameter * 0.5))
  ctx.font = `bold ${fontPx}px -apple-system, "SF Pro Text", sans-serif`
  ctx.fillStyle = '#ffffff'
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'center'
  ctx.fillText(chip.initials, cx, cy + 1)
  ctx.textAlign = 'start'
}

/** Apply a global text scale to every label sprite under `root`. */
export function applyLabelScale(root: Object3D, scale: number): void {
  root.traverse((child) => {
    const ud = (child as Sprite).userData as { baseScaleX?: number; baseScaleY?: number } | undefined
    if (ud && typeof ud.baseScaleX === 'number' && typeof ud.baseScaleY === 'number') {
      ;(child as Sprite).scale.set(ud.baseScaleX * scale, ud.baseScaleY * scale, 1)
    }
  })
}

export function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

export function disposeChildren(parent: Object3D): void {
  parent.traverse((node) => {
    const geom = (node as { geometry?: { dispose?: () => void } }).geometry
    if (geom && typeof geom.dispose === 'function') geom.dispose()
    const mat = (node as { material?: { dispose?: () => void } | { dispose?: () => void }[] }).material
    if (Array.isArray(mat)) mat.forEach((mm) => mm.dispose?.())
    else if (mat && typeof (mat as { dispose?: () => void }).dispose === 'function') {
      ;(mat as { dispose: () => void }).dispose()
    }
  })
}
